/* =====================================================================
   AstaLab 2.0 — joinCode.js (round alpha.4-r1, corretto in alpha.4-r2 — BLOCKER C)

   Canonicalizzazione di un codice di invito digitato dall'utente — funzione pura, condivisa da
   entrambi gli adapter (SupabaseBackendAdapter/ArtifactDemoBackendAdapter) e dalla UI (validazione
   "sembra un codice" prima di inviarlo). DEVE restare semanticamente identica, per QUALUNQUE input,
   a private.canonicalize_join_code() in
   backend/migrations/0012_multiplayer_lobby_foundation.sql — vedi
   MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md §2.3. tests/test_multiplayer_lobby_alpha4_r1.js confronta i
   due comportamenti su una batteria di input (inclusi caratteri Unicode ambigui/espandibili sotto
   uppercase, emoji, accenti).

   BLOCKER C (audit indipendente su r1): PRIMA di questa correzione, questo file applicava
   .toUpperCase() PRIMA del filtro [^A-Z0-9] (uppercase → filtra), mentre 0012's
   private.canonicalize_join_code() applicava upper(regexp_replace(...)) — cioè filtra → uppercase
   (l'ordine SQL era già corretto; solo QUESTO file aveva l'ordine sbagliato). L'ordine conta perché
   .toUpperCase() di JS segue il case-folding Unicode completo, non un mapping ASCII 1:1: alcuni
   caratteri si ESPANDONO in più caratteri ASCII sotto uppercase (es. "ß".toUpperCase() === "SS" in
   JS), quindi filtrare DOPO l'uppercase può lasciare residui ASCII (le due "S") che filtrare PRIMA
   avrebbe invece rimosso insieme al carattere originale (mai ASCII, quindi già escluso dal filtro).
   Filtrando SEMPRE a [A-Za-z0-9] PRIMA di qualunque case-folding, solo caratteri ASCII puri
   raggiungono mai lo step di uppercase — dove .toUpperCase() e upper() di Postgres si comportano
   IDENTICAMENTE (mapping ASCII a-z -> A-Z 1:1, mai un'espansione multi-carattere possibile per un
   input già ristretto ad ASCII). Questo garantisce l'equivalenza per qualunque input, non solo per
   quelli "normali" già provati prima di questo round.

   L'hash NON è condiviso di proposito: lato SQL usa md5() su entropia gen_random_uuid() (vincolo
   search_path='', vedi §2.2 del design doc, rivisto in alpha.4-r2 — BLOCKER B), lato demo usa SHA-256
   su entropia crypto.getRandomValues() via WebCrypto (nessun vincolo di schema nel browser) — sono due
   implementazioni indipendenti dello stesso comportamento (canonicalizza → hash → confronta), non due
   repliche dello stesso valore: nessun test deve mai confrontare un hash SQL con uno demo.
   ===================================================================== */

/** @returns {string} solo A-Z0-9, maiuscolo, senza spazi/trattini/altro — mai un throw, un input
 *  vuoto/non-stringa produce una stringa vuota (il chiamante decide come trattarla).
 *  ORDINE OBBLIGATORIO (BLOCKER C): filtra a [A-Za-z0-9] PRIMA, uppercase DOPO — mai il contrario,
 *  altrimenti un carattere Unicode che si espande sotto case-folding (es. "ß" -> "SS") può lasciare
 *  residui ASCII che l'ordine inverso (usato erroneamente da questo file prima di alpha.4-r2)
 *  avrebbe prodotto, divergendo dal comportamento SQL. */
export function canonicalizeJoinCode(raw){
  return String(raw == null ? "" : raw).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/** Formatta un codice canonico in blocchi da 4 separati da un trattino, per la sola visualizzazione
 *  (mai usato per il confronto/hash — quello usa sempre canonicalizeJoinCode()). Codici di lunghezza
 *  diversa da 12 (es. generati con una lunghezza futura diversa, o un valore parziale ancora in
 *  digitazione) sono restituiti non raggruppati — BLOCKER B (alpha.4-r2): il codice è ora a 12
 *  caratteri/60 bit di entropia (era 10/50 bit in r1), formattato in tre blocchi da 4. */
export function formatJoinCodeForDisplay(canonical){
  const c = String(canonical || "");
  if(c.length !== 12) return c;
  return c.slice(0,4) + "-" + c.slice(4,8) + "-" + c.slice(8);
}

/** Validazione "sembra un codice" lato client, prima di inviarlo — mai l'unica validazione: il
 *  server/adapter resta l'autorità (un codice sintatticamente valido ma sconosciuto/revocato viene
 *  comunque rifiutato più a valle). Serve solo per un messaggio d'errore immediato su un campo
 *  palesemente vuoto o troppo corto, non per indovinare l'alfabeto esatto lato client. */
export function looksLikeJoinCode(raw){
  const c = canonicalizeJoinCode(raw);
  return c.length >= 6;
}
